import React, { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc, setDoc, collection, getDocs, deleteDoc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase/config';
import toast from 'react-hot-toast';
import AvatarPicker from './AvatarPicker';
import { AVATAR_CATEGORIES, isValidAvatarUrl, getDefaultAvatar } from '../utils/avatarData';
import { CartoonAvatar } from './GamificationAssets';

export default function ProfileSettings({ myProfile }) {
    // --- PERSONAL PROFILE STATES ---
    const [name, setName] = useState('');
    const [avatar, setAvatar] = useState('');
    const [boardTheme, setBoardTheme] = useState('green');
    const [pieceTheme, setPieceTheme] = useState('cburnett');
    const [meetLink, setMeetLink] = useState(''); 
    const [displayUsername, setDisplayUsername] = useState(''); // 🔥 NAYA STATE
    const [activeCategory, setActiveCategory] = useState('students');
    const [isPickerOpen, setIsPickerOpen] = useState(false);

    // --- ACADEMY RESET STATES (ONLY FOR OWNER) ---
    const [confirmText, setConfirmText] = useState('');
    const [isPurging, setIsPurging] = useState(false);
    const [purgeHW, setPurgeHW] = useState(true);
    const [purgeClasses, setPurgeClasses] = useState(true);
    const [purgeGames, setPurgeGames] = useState(true);
    const [purgeFinance, setPurgeFinance] = useState(true);
    const [resetStudents, setResetStudents] = useState(true);

    const handlePurgeData = async (e) => {
        e.preventDefault();
        if (confirmText !== 'RESET') {
            return toast.error("Please type 'RESET' in the confirmation box to proceed.");
        }
        
        setIsPurging(true);
        const loading = toast.loading("Purging selected database records...");
        
        try {
            // 1. Purge Homeworks and Submissions
            if (purgeHW) {
                const hwSnap = await getDocs(collection(db, "homeworks"));
                const hwPromises = hwSnap.docs.map(d => deleteDoc(doc(db, "homeworks", d.id)));
                await Promise.all(hwPromises);
                
                const subSnap = await getDocs(collection(db, "homework_submissions"));
                const subPromises = subSnap.docs.map(d => deleteDoc(doc(db, "homework_submissions", d.id)));
                await Promise.all(subPromises);
            }
            
            // 2. Purge Classes
            if (purgeClasses) {
                const clSnap = await getDocs(collection(db, "classes"));
                const clPromises = clSnap.docs.map(d => deleteDoc(doc(db, "classes", d.id)));
                await Promise.all(clPromises);
            }
            
            // 3. Purge Arena Games
            if (purgeGames) {
                const gmSnap = await getDocs(collection(db, "games"));
                const gmPromises = gmSnap.docs.map(d => deleteDoc(doc(db, "games", d.id)));
                await Promise.all(gmPromises);
            }
            
            // 4. Purge Financial Logs
            if (purgeFinance) {
                const paySnap = await getDocs(collection(db, "payroll_logs"));
                const payPromises = paySnap.docs.map(d => deleteDoc(doc(db, "payroll_logs", d.id)));
                await Promise.all(payPromises);

                const crSnap = await getDocs(collection(db, "credit_logs"));
                const crPromises = crSnap.docs.map(d => deleteDoc(doc(db, "credit_logs", d.id)));
                await Promise.all(crPromises);
            }
            
            // 5. Reset Students
            if (resetStudents) {
                const usersSnap = await getDocs(collection(db, "users"));
                const batch = writeBatch(db);
                let count = 0;
                
                usersSnap.docs.forEach(uDoc => {
                    const data = uDoc.data();
                    if (data.role === 'STUDENT') {
                        const userRef = doc(db, "users", uDoc.id);
                        batch.update(userRef, {
                            completedMissions: [],
                            credits: 0,
                            coins: 0,
                            stars: 0,
                            elo: 1200,
                            online: false,
                            lastSeen: null,
                            activeMinutes: 0
                        });
                        count++;
                    }
                });
                if (count > 0) {
                    await batch.commit();
                }
            }
            
            toast.success("Database purged successfully! 🧹✨", { id: loading });
            setConfirmText('');
        } catch (err) {
            console.error("Failed to purge data:", err);
            toast.error("Database purge failed. Check console for details.", { id: loading });
        } finally {
            setIsPurging(false);
        }
    };

    // --- GLOBAL ACADEMY STATES (ONLY FOR OWNER) ---
    const [upiId, setUpiId] = useState('');
    const [supportContact, setSupportContact] = useState('');
    const [announcement, setAnnouncement] = useState('');

    useEffect(() => {
        if (myProfile) {
            setName(myProfile.name || '');
            setAvatar(isValidAvatarUrl(myProfile.avatar) ? myProfile.avatar : getDefaultAvatar(myProfile.name || myProfile.email));
            setBoardTheme(myProfile.boardTheme || 'green');
            setPieceTheme(myProfile.pieceTheme || 'cburnett');
            setMeetLink(myProfile.meetLink || '');
            
            // 🔥 Extracting Username from Email
            if (myProfile.email) {
                setDisplayUsername(myProfile.username || myProfile.email.split('@')[0]);
            }
            
            if (myProfile.role === 'OWNER') {
                fetchGlobalSettings();
            }
        }
    }, [myProfile]);

    const fetchGlobalSettings = async () => {
        try {
            const docRef = doc(db, "academy_settings", "global");
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const data = docSnap.data();
                setUpiId(data.upiId || '');
                setSupportContact(data.supportContact || '');
                setAnnouncement(data.announcement || '');
            }
        } catch (error) {
            console.error("Failed to fetch global settings", error);
        }
    };

    const handleSavePersonal = async (e) => {
        e.preventDefault();
        if (!myProfile?.email) return;
        
        const loading = toast.loading("Saving personal profile...");
        try {
            await updateDoc(doc(db, "users", myProfile.email), {
                name, avatar, boardTheme, pieceTheme, meetLink
            });
            toast.success("Profile updated successfully!", { id: loading });
        } catch (err) {
            toast.error("Failed to update profile.", { id: loading });
        }
    };

    const handleSaveGlobal = async (e) => {
        e.preventDefault();
        const loading = toast.loading("Saving Academy Settings...");
        try {
            await setDoc(doc(db, "academy_settings", "global"), {
                upiId, supportContact, announcement, updatedAt: new Date().toISOString()
            }, { merge: true });
            toast.success("Global Settings Live! 🌍", { id: loading });
        } catch (err) {
            toast.error("Failed to save global settings.", { id: loading });
        }
    };

    return (
        <div style={{display: 'flex', flexDirection: 'column', gap: '30px', width: '100%' }}>
            
            <div className="clay-card" style={{ background: 'var(--bg-primary)', padding: '30px', borderRadius: '28px', border: '3px solid #ffffff', boxShadow: 'var(--panel-shadow)', width: '100%', boxSizing: 'border-box' }}>
                <h2 style={{ color: '#3b82f6', margin: '0 0 20px 0', fontWeight: '900' }}>👤 My Profile Settings</h2>
                
                <form onSubmit={handleSavePersonal} style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '600px' }}>
                    
                    {/* Avatar Selection */}
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <label style={{ color: 'var(--text-secondary)', fontSize: '14px', fontWeight: 'bold' }}>
                                Choose Profile Avatar Category
                            </label>
                            <button
                                type="button"
                                onClick={() => setIsPickerOpen(true)}
                                style={{
                                    padding: '6px 14px', background: '#3b82f6', color: 'white',
                                    border: 'none', borderRadius: '10px', fontSize: '12px', fontWeight: 'bold',
                                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
                                    boxShadow: '0 3px 10px rgba(59,130,246,0.3)'
                                }}
                            >
                                ✨ Open Avatar Studio Modal
                            </button>
                        </div>

                        {/* Selected Avatar Preview Box */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', background: 'var(--bg-secondary)', padding: '12px 18px', borderRadius: '16px', border: '1px solid rgba(163,177,198,0.3)', marginBottom: '15px' }}>
                            <CartoonAvatar name={name || 'User'} size={56} avatarUrl={avatar} />
                            <div>
                                <span style={{ fontSize: '11px', color: '#3b82f6', fontWeight: 800, textTransform: 'uppercase' }}>Selected Avatar</span>
                                <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>
                                    {name || 'My Profile'} Avatar
                                </div>
                            </div>
                        </div>

                        {/* Category Tabs */}
                        <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '8px', marginBottom: '12px' }}>
                            {AVATAR_CATEGORIES.map(cat => {
                                const isCatActive = activeCategory === cat.id;
                                return (
                                    <button
                                        key={cat.id}
                                        type="button"
                                        onClick={() => setActiveCategory(cat.id)}
                                        style={{
                                            padding: '8px 14px', borderRadius: '12px',
                                            background: isCatActive ? '#3b82f6' : 'var(--bg-primary)',
                                            color: isCatActive ? 'white' : 'var(--text-primary)',
                                            border: isCatActive ? '2px solid white' : '1.5px solid rgba(163, 177, 198, 0.4)',
                                            fontSize: '12px', fontWeight: 'bold', cursor: 'pointer',
                                            whiteSpace: 'nowrap', transition: 'all 0.2s',
                                            boxShadow: isCatActive ? 'var(--neu-shadow-inset)' : 'var(--neu-shadow)'
                                        }}
                                    >
                                        {cat.label}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Active Category Avatar Grid */}
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', maxHeight: '180px', overflowY: 'auto', padding: '5px' }}>
                            {(AVATAR_CATEGORIES.find(c => c.id === activeCategory)?.avatars || []).map(item => {
                                const isSel = avatar === item.url;
                                return (
                                    <div
                                        key={item.id}
                                        onClick={() => setAvatar(item.url)}
                                        title={item.name}
                                        style={{
                                            background: isSel ? '#3b82f6' : 'var(--bg-primary)',
                                            padding: '4px', borderRadius: '50%', cursor: 'pointer',
                                            border: isSel ? '3px solid white' : '1.5px solid rgba(163, 177, 198, 0.4)',
                                            transition: 'all 0.2s ease',
                                            boxShadow: isSel ? '0 0 15px rgba(59,130,246,0.4)' : 'var(--neu-shadow)'
                                        }}
                                    >
                                        <CartoonAvatar name={item.name} size={48} avatarUrl={item.url} />
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {isPickerOpen && (
                        <AvatarPicker
                            currentUrl={avatar}
                            onSelect={(newUrl) => { setAvatar(newUrl); setIsPickerOpen(false); }}
                            onClose={() => setIsPickerOpen(false)}
                        />
                    )}

                    {/* 🔥 READ-ONLY USERNAME BOX */}
                    <div className="glass-panel" style={{ background: 'rgba(16, 185, 129, 0.05)', padding: '18px 24px', borderRadius: '16px', border: '2px solid rgba(16, 185, 129, 0.35)', boxShadow: 'var(--neu-shadow-inset)' }}>
                        <label style={{ color: '#10b981', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Login Username</label>
                        <input type="text" value={displayUsername} disabled style={{ width: '100%', padding: '12px', background: 'transparent', border: 'none', color: '#10b981', fontSize: '16px', fontWeight: 'bold', boxSizing: 'border-box', outline: 'none' }} />
                        <p style={{ color: 'var(--text-secondary)', fontSize: '11px', margin: '5px 0 0 0', fontWeight: 'bold' }}>This is your official Academy Username. Contact your Coach to change your Password.</p>
                    </div>

                    {/* Name Input */}
                    <div>
                        <label style={{ color: 'var(--text-secondary)', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Display Name</label>
                        <input type="text" value={name} onChange={e => setName(e.target.value)} required style={{ width: '100%', padding: '14px 18px', background: 'var(--bg-primary)', border: '1.5px solid rgba(163, 177, 198, 0.4)', borderRadius: '12px', color: 'var(--text-primary)', fontSize: '15px', boxSizing: 'border-box', outline: 'none', boxShadow: 'var(--neu-shadow-inset)', transition: 'all 0.3s ease' }} />
                    </div>

                    {/* Google Meet Link */}
                    {myProfile?.role !== 'STUDENT' && (
                        <div className="glass-panel" style={{ background: 'var(--bg-primary)', padding: '20px', borderRadius: '18px', border: '2px solid rgba(163, 177, 198, 0.4)', boxShadow: 'var(--panel-shadow)' }}>
                            <label style={{ color: '#3b82f6', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>🎥 Personal Meeting Link (Google Meet / Zoom)</label>
                            <input type="url" value={meetLink} onChange={e => setMeetLink(e.target.value)} placeholder="https://meet.google.com/xyz" style={{ width: '100%', padding: '14px 18px', background: 'var(--bg-primary)', border: '1.5px solid #3b82f6', borderRadius: '12px', color: 'var(--text-primary)', fontSize: '15px', boxSizing: 'border-box', outline: 'none', boxShadow: 'var(--neu-shadow-inset)' }} />
                            <p style={{ color: 'var(--text-secondary)', fontSize: '12px', margin: '8px 0 0 0', fontStyle: 'italic', fontWeight: 'bold' }}>This link will be auto-filled whenever you schedule a new class.</p>
                        </div>
                    )}

                    {/* Board Theme Defaults */}
                    <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                        <div style={{ flex: '1 1 200px' }}>
                            <label style={{ color: 'var(--text-secondary)', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Default Board Theme</label>
                            <select value={boardTheme} onChange={e => setBoardTheme(e.target.value)} style={{ width: '100%', padding: '14px 18px', background: 'var(--bg-primary)', border: '1.5px solid rgba(163, 177, 198, 0.4)', borderRadius: '12px', color: 'var(--text-primary)', fontSize: '15px', outline: 'none', boxShadow: 'var(--neu-shadow)' }}>
                                <option value="green">🌿 Green (Lichess)</option>
                                <option value="wood">🪵 Wood (Lichess)</option>
                                <option value="blue">🌊 Blue (Lichess)</option>
                                <option value="sand">⛱️ Sand (Lichess)</option>
                                <option value="canvas">🎨 Canvas (Lichess)</option>
                                <option value="maple">🍁 Maple (Lichess)</option>
                                <option value="ic">❄️ Icy (Lichess)</option>
                                <option value="grey">🩶 Grey (Lichess)</option>
                                <option value="metal">🪙 Metal (Lichess)</option>
                                <option value="olive">🫒 Olive (Lichess)</option>
                                <option value="purple">🔮 Purple (Lichess)</option>
                                <option value="pink">🌸 Pink (Lichess)</option>
                                <option value="horn">📯 Horn (Lichess)</option>
                                <option value="newspaper">📰 Newspaper (Lichess)</option>
                            </select>
                        </div>
                        <div style={{ flex: '1 1 200px' }}>
                            <label style={{ color: 'var(--text-secondary)', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Default Piece Theme</label>
                            <select value={pieceTheme} onChange={e => setPieceTheme(e.target.value)} style={{ width: '100%', padding: '14px 18px', background: 'var(--bg-primary)', border: '1.5px solid rgba(163, 177, 198, 0.4)', borderRadius: '12px', color: 'var(--text-primary)', fontSize: '15px', outline: 'none', boxShadow: 'var(--neu-shadow)' }}>
                                <option value="cburnett">♟️ Classic (Cburnett)</option>
                                <option value="merida">♟️ Merida</option>
                                <option value="alpha">♟️ Alpha</option>
                                <option value="maestro">♟️ Maestro</option>
                                <option value="staunty">♟️ Staunty</option>
                                <option value="caliente">♟️ Caliente</option>
                                <option value="california">♟️ California</option>
                                <option value="cardinal">♟️ Cardinal</option>
                                <option value="dubrovny">♟️ Dubrovny</option>
                                <option value="fantasy">♟️ Fantasy</option>
                                <option value="fresca">♟️ Fresca</option>
                                <option value="governor">♟️ Governor</option>
                                <option value="horsey">🐴 Horsey</option>
                                <option value="kiwen-suwi">♟️ Kiwen Suwi</option>
                                <option value="kosal">♟️ Kosal</option>
                                <option value="leipzig">♟️ Leipzig</option>
                                <option value="letter">♟️ Letter</option>
                                <option value="pixel">♟️ Pixel</option>
                                <option value="riohacha">♟️ Riohacha</option>
                                <option value="shapes">♟️ Shapes</option>
                                <option value="spatial">♟️ Spatial</option>
                                <option value="tatiana">♟️ Tatiana</option>
                                <option value="pirouetti">♟️ Pirouetti</option>
                                <option value="chess7">♟️ Chess7</option>
                                <option value="companion">♟️ Companion</option>
                                <option value="celtic">♟️ Celtic</option>
                            </select>
                        </div>
                    </div>

                    <button type="submit" className="clay-btn" style={{ padding: '14px 28px', background: '#3b82f6', color: 'white', border: '3px solid #ffffff', fontWeight: '900', fontSize: '15px', cursor: 'pointer', marginTop: '10px', boxShadow: 'var(--panel-shadow)' }}>
                        💾 Save Personal Settings
                    </button>
                </form>
            </div>

            {/* 👑 ACADEMY GLOBAL SETTINGS (ONLY FOR OWNER) */}
            {myProfile?.role === 'OWNER' && (
                <div className="clay-card" style={{ background: 'var(--bg-primary)', padding: '30px', borderRadius: '28px', border: '3px solid #ffffff', boxShadow: 'var(--panel-shadow)', width: '100%', boxSizing: 'border-box' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                        <span style={{ fontSize: '30px' }}>👑</span>
                        <h2 style={{ color: '#a855f7', margin: 0, fontWeight: '900' }}>Academy Global Settings</h2>
                    </div>
                    
                    <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '20px', fontWeight: 'bold' }}>
                        Changes made here will reflect globally across all student and coach accounts in the Hayaa Chess Academy platform.
                    </p>

                    <form onSubmit={handleSaveGlobal} style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '600px' }}>
                        
                        {/* Global Announcement */}
                        <div>
                            <label style={{ color: 'var(--text-secondary)', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>📢 Global Announcement Board</label>
                            <textarea 
                                value={announcement} 
                                onChange={e => setAnnouncement(e.target.value)} 
                                placeholder="e.g. Next weekend is a holiday! Happy Diwali!" 
                                rows="3"
                                style={{ width: '100%', padding: '14px 18px', background: 'var(--bg-primary)', border: '1.5px solid rgba(163, 177, 198, 0.4)', borderRadius: '12px', color: 'var(--text-primary)', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical', outline: 'none', boxShadow: 'var(--neu-shadow-inset)' }} 
                            />
                        </div>

                        {/* UPI / Bank Details */}
                        <div>
                            <label style={{ color: 'var(--text-secondary)', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>💳 Official Payment UPI / Details</label>
                            <input 
                                type="text" 
                                value={upiId} 
                                onChange={e => setUpiId(e.target.value)} 
                                placeholder="e.g. hayaachess@okicici"
                                style={{ width: '100%', padding: '14px 18px', background: 'var(--bg-primary)', border: '1.5px solid rgba(163, 177, 198, 0.4)', borderRadius: '12px', color: 'var(--text-primary)', fontSize: '15px', boxSizing: 'border-box', outline: 'none', boxShadow: 'var(--neu-shadow-inset)' }} 
                            />
                        </div>

                        {/* Support Contact */}
                        <div>
                            <label style={{ color: 'var(--text-secondary)', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>📞 Official Support Contact</label>
                            <input 
                                type="text" 
                                value={supportContact} 
                                onChange={e => setSupportContact(e.target.value)} 
                                placeholder="e.g. +91 9876543210 or support@hayaa.com"
                                style={{ width: '100%', padding: '14px 18px', background: 'var(--bg-primary)', border: '1.5px solid rgba(163, 177, 198, 0.4)', borderRadius: '12px', color: 'var(--text-primary)', fontSize: '15px', boxSizing: 'border-box', outline: 'none', boxShadow: 'var(--neu-shadow-inset)' }} 
                            />
                        </div>

                        <button type="submit" className="clay-btn" style={{ padding: '14px 28px', background: '#a855f7', color: 'white', border: '3px solid #ffffff', fontWeight: '900', fontSize: '15px', cursor: 'pointer', marginTop: '10px', boxShadow: 'var(--panel-shadow)' }}>
                            🚀 Publish Global Settings
                        </button>
                    </form>
                </div>
            )}
            
            {/* 🧹 ACADEMY DATA PURGE PANEL (ONLY FOR OWNER) */}
            {myProfile?.role === 'OWNER' && (
                <div className="clay-card" style={{ background: 'var(--bg-primary)', padding: '30px', borderRadius: '28px', border: '3px dashed #ef4444', boxShadow: 'var(--panel-shadow)', width: '100%', boxSizing: 'border-box' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                        <span style={{ fontSize: '30px' }}>🧹</span>
                        <h2 style={{ color: '#ef4444', margin: 0, fontWeight: '900' }}>Academy Reset & Clean-up (Pre-Launch)</h2>
                    </div>
                    
                    <div style={{ background: '#fef2f2', border: '2px solid #fee2e2', padding: '15px 20px', borderRadius: '16px', color: '#b91c1c', fontSize: '13px', fontWeight: '750', marginBottom: '25px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <span>⚠️ WARNING: These tools are for purging testing data before launching your Academy.</span>
                        <span>This will delete all logs, history, and test tasks. This action is PERMANENT and CANNOT be undone.</span>
                    </div>

                    <form onSubmit={handlePurgeData} style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '600px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', background: 'var(--bg-primary)', boxShadow: 'var(--neu-shadow-inset)', padding: '20px', borderRadius: '16px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', fontWeight: '800', color: 'var(--text-primary)', cursor: 'pointer' }}>
                                <input type="checkbox" checked={purgeHW} onChange={e => setPurgeHW(e.target.checked)} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                                📝 Purge Test Homeworks & Submissions
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', fontWeight: '800', color: 'var(--text-primary)', cursor: 'pointer' }}>
                                <input type="checkbox" checked={purgeClasses} onChange={e => setPurgeClasses(e.target.checked)} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                                🏫 Purge Class Schedules & Meeting History
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', fontWeight: '800', color: 'var(--text-primary)', cursor: 'pointer' }}>
                                <input type="checkbox" checked={purgeGames} onChange={e => setPurgeGames(e.target.checked)} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                                ⚔️ Purge Arena Games & Challenge History
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', fontWeight: '800', color: 'var(--text-primary)', cursor: 'pointer' }}>
                                <input type="checkbox" checked={purgeFinance} onChange={e => setPurgeFinance(e.target.checked)} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                                💸 Purge Financial Payout Logs & Coin Transactions
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', fontWeight: '800', color: 'var(--text-primary)', cursor: 'pointer' }}>
                                <input type="checkbox" checked={resetStudents} onChange={e => setResetStudents(e.target.checked)} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                                👤 Reset Student Progress (ELO to 1200, Puzzles Completed to 0)
                            </label>
                        </div>

                        <div style={{ marginTop: '10px' }}>
                            <label style={{ color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '800', display: 'block', marginBottom: '8px' }}>
                                Type <span style={{ color: '#ef4444', fontWeight: '900' }}>"RESET"</span> to confirm authorization:
                            </label>
                            <input 
                                type="text"
                                value={confirmText}
                                onChange={e => setConfirmText(e.target.value)}
                                placeholder="RESET"
                                disabled={isPurging}
                                style={{ width: '100%', padding: '14px 18px', background: 'var(--bg-primary)', border: '1.5px solid rgba(163, 177, 198, 0.4)', borderRadius: '12px', color: '#ef4444', fontSize: '15px', fontWeight: '900', letterSpacing: '2px', boxSizing: 'border-box', outline: 'none', boxShadow: 'var(--neu-shadow-inset)' }}
                            />
                        </div>

                        <button 
                            type="submit" 
                            disabled={isPurging || confirmText !== 'RESET'}
                            className="clay-btn"
                            style={{
                                padding: '15px', 
                                background: confirmText === 'RESET' ? '#ef4444' : 'var(--text-secondary)', 
                                color: 'white', 
                                border: '3px solid #ffffff', 
                                borderRadius: '50px', 
                                fontWeight: '900', 
                                fontSize: '16px', 
                                cursor: confirmText === 'RESET' ? 'pointer' : 'not-allowed', 
                                marginTop: '10px', 
                                boxShadow: confirmText === 'RESET' ? 'var(--panel-shadow)' : 'none',
                                opacity: isPurging ? 0.7 : 1,
                                transition: 'all 0.2s'
                            }}
                        >
                            {isPurging ? '🧹 Purging Database...' : '🧹 Purge Selected Testing Data'}
                        </button>
                    </form>
                </div>
            )}

        </div>
    );
}
